import { useId, type ReactElement } from 'react';

import { TextField } from '@/components/ui/TextField';

import { fieldLabel, type CustomFieldItem } from '../presentation';
import { Select } from '@/components/ui/Select';

// Control de un campo propio según su tipo, con su error enlazado (aria-describedby).
// Texto y número usan TextField; fecha, sí/no y lista usan el control nativo con el
// mismo estilo de campo. Un obligatorio se nombra "(obligatorio)" en su etiqueta.

export function CustomFieldControl({
  field,
  value,
  error,
  onChange,
}: {
  field: CustomFieldItem;
  value: string;
  error: string | null;
  onChange: (value: string) => void;
}): ReactElement {
  const id = useId();
  const errorId = `${id}-err`;
  const label = fieldLabel(field);
  if (field.dataType === 'text' || field.dataType === 'number') {
    return (
      <TextField
        label={label}
        value={value}
        maxLength={2000}
        inputMode={field.dataType === 'number' ? 'decimal' : 'text'}
        hint={
          field.dataType === 'number'
            ? 'Coma o punto para decimales, sin separador de miles'
            : undefined
        }
        error={error}
        onChange={onChange}
      />
    );
  }
  const common = {
    id,
    className: 'selector fw',
    value,
    'aria-invalid': error !== null,
    'aria-describedby': error === null ? undefined : errorId,
  };
  return (
    <div className="fld">
      <label htmlFor={id} className="fld-lab">
        {label}
      </label>
      {field.dataType === 'date' ? (
        <input {...common} type="date" onChange={(e) => onChange(e.target.value)} />
      ) : (
        <Select
          id={id}
          value={value}
          invalid={error !== null}
          ariaDescribedBy={error === null ? undefined : errorId}
          // Un valor guardado que ya no está entre las opciones se muestra tal cual.
          placeholder={value === '' ? 'Sin completar' : value}
          options={[
            { value: '', label: 'Sin completar' },
            ...(field.dataType === 'boolean'
              ? [
                  { value: 'si', label: 'Sí' },
                  { value: 'no', label: 'No' },
                ]
              : (field.options ?? []).map((o) => ({ value: o, label: o }))),
          ]}
          onChange={onChange}
        />
      )}
      {error !== null && (
        <span id={errorId} className="fld-err">
          {error}
        </span>
      )}
    </div>
  );
}
